import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, Facebook, Instagram, Linkedin, Twitter, Wifi, WifiOff, Clock,
  CheckCircle, AlertCircle, FileEdit, Send, ThumbsUp, MessageSquare,
  CalendarDays, Hash, X, Pencil, Trash2, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = ['Facebook', 'Instagram', 'LinkedIn', 'X (Twitter)'];
const STATUS_OPTIONS = ['draft', 'pending_approval', 'approved', 'changes_requested', 'scheduled', 'posted'];

const platformIcon = (p) => {
  if (p === 'Facebook') return Facebook;
  if (p === 'Instagram') return Instagram;
  if (p === 'LinkedIn') return Linkedin;
  return Twitter;
};

const platformColor = (p) => {
  if (p === 'Facebook') return 'from-blue-500 to-blue-600';
  if (p === 'Instagram') return 'from-pink-500 to-purple-500';
  if (p === 'LinkedIn') return 'from-sky-600 to-blue-700';
  return 'from-zinc-700 to-zinc-800';
};

const statusConfig = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600', icon: FileEdit },
  pending_approval: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  changes_requested: { label: 'Changes Requested', cls: 'bg-red-100 text-red-700', icon: AlertCircle },
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700', icon: CalendarDays },
  posted: { label: 'Posted', cls: 'bg-violet-100 text-violet-700', icon: Send },
};

// ─── ACCOUNT ACCESS TAB ───
function AccountAccess({ firmId, user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isClient = user.role === 'client';

  useEffect(() => { fetchAccounts(); }, [firmId]);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/social/accounts/${firmId}`, { headers });
      setAccounts(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (accountId, newStatus) => {
    try {
      await axios.patch(`${API}/social/accounts/${accountId}`, { status: newStatus }, { headers });
      fetchAccounts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const statusLabel = { not_connected: 'Not Connected', pending_access: 'Pending Access', connected: 'Connected' };
  const statusCls = { not_connected: 'bg-slate-100 text-slate-600', pending_access: 'bg-amber-100 text-amber-700', connected: 'bg-emerald-100 text-emerald-700' };
  const statusIco = { not_connected: WifiOff, pending_access: Clock, connected: Wifi };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading accounts...</div>;

  return (
    <div className="space-y-6" data-testid="social-account-access">
      <div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Connect Your Social Accounts</h2>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          To manage your social media, please grant access to your social accounts using the email below. Once access is granted, our team will update the status here.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-200 rounded-xl">
          <span className="text-sm font-medium text-violet-700">social@yourdomain.com</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.map((acc) => {
          const Icon = platformIcon(acc.platform);
          const SIcon = statusIco[acc.status] || WifiOff;
          return (
            <div key={acc.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`account-${acc.platform.toLowerCase().replace(/[() ]/g, '')}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${platformColor(acc.platform)} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{acc.platform}</div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${statusCls[acc.status]}`}>
                      <SIcon className="w-3 h-3" />{statusLabel[acc.status]}
                    </span>
                  </div>
                </div>
                {!isClient && (
                  <select
                    value={acc.status}
                    onChange={(e) => updateStatus(acc.id, e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                    data-testid={`status-select-${acc.platform.toLowerCase().replace(/[() ]/g, '')}`}
                  >
                    <option value="not_connected">Not Connected</option>
                    <option value="pending_access">Pending Access</option>
                    <option value="connected">Connected</option>
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── POST FORM MODAL ───
function PostFormModal({ post, onClose, onSave, firmId }) {
  const [form, setForm] = useState(post || {
    title: '', platform: 'Facebook', caption: '', media_url: '', hashtags: '', scheduled_date: '', notes: '', status: 'draft'
  });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.caption.trim()) { alert('Title and caption required'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (post?.id) {
        await axios.put(`${API}/social/posts/${post.id}`, form, { headers });
      } else {
        await axios.post(`${API}/social/posts/${firmId}`, form, { headers });
      }
      onSave();
    } catch (err) { alert(err.response?.data?.detail || 'Error saving'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="post-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{post?.id ? 'Edit Post' : 'Add Social Post'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Post Title</label>
            <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm" placeholder="Post title" data-testid="post-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Platform</label>
            <select value={form.platform} onChange={(e) => setForm(f => ({ ...f, platform: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm" data-testid="post-platform-select">
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Caption</label>
            <textarea value={form.caption} onChange={(e) => setForm(f => ({ ...f, caption: e.target.value }))} rows={4} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm resize-none" placeholder="Write caption..." data-testid="post-caption-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Media URL</label>
              <input value={form.media_url} onChange={(e) => setForm(f => ({ ...f, media_url: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm" placeholder="https://..." data-testid="post-media-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Scheduled Date</label>
              <input type="datetime-local" value={form.scheduled_date?.slice(0, 16) || ''} onChange={(e) => setForm(f => ({ ...f, scheduled_date: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm" data-testid="post-date-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hashtags</label>
            <input value={form.hashtags} onChange={(e) => setForm(f => ({ ...f, hashtags: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm" placeholder="#tag1 #tag2" data-testid="post-hashtags-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm resize-none" placeholder="Internal notes..." data-testid="post-notes-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm" data-testid="post-status-select">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusConfig[s]?.label || s}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="post-save-btn">{saving ? 'Saving...' : (post?.id ? 'Update Post' : 'Create Post')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── FEEDBACK MODAL ───
function FeedbackModal({ onClose, onSubmit }) {
  const [text, setText] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="feedback-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Suggest Changes</h3>
        <p className="text-sm text-slate-500 mb-4">Enter your requested changes below.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm resize-none mb-4" placeholder="Describe the changes you'd like..." data-testid="feedback-textarea" />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
          <button onClick={() => { if (text.trim()) onSubmit(text); }} className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:shadow-lg" data-testid="feedback-submit-btn">Submit Feedback</button>
        </div>
      </div>
    </div>
  );
}

// ─── POSTS TAB ───
function PostsTab({ firmId, user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [feedbackPost, setFeedbackPost] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isClient = user.role === 'client';

  useEffect(() => { fetchPosts(); }, [firmId]);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/social/posts/${firmId}`, { headers });
      setPosts(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/social/seed/${firmId}`, {}, { headers });
      fetchPosts();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try { await axios.delete(`${API}/social/posts/${postId}`, { headers }); fetchPosts(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const notifyApproval = async (postId) => {
    try {
      await axios.put(`${API}/social/posts/${postId}`, { status: 'pending_approval' }, { headers });
      alert('Client notified for approval.');
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const approvePost = async (postId) => {
    try {
      await axios.put(`${API}/social/posts/${postId}`, { status: 'approved' }, { headers });
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const submitFeedback = async (text) => {
    try {
      await axios.put(`${API}/social/posts/${feedbackPost}`, { status: 'changes_requested', feedback: text }, { headers });
      setFeedbackPost(null);
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading posts...</div>;

  return (
    <div className="space-y-6" data-testid="social-posts">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Social Posts</h2>
        {!isClient && (
          <div className="flex items-center gap-3">
            {posts.length === 0 && (
              <button onClick={seedData} className="px-4 py-2 text-sm text-violet-600 border border-violet-200 rounded-xl hover:bg-violet-50 font-medium transition-colors" data-testid="seed-data-btn">
                Load Sample Data
              </button>
            )}
            <button onClick={() => { setEditingPost(null); setShowForm(true); }} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30" data-testid="add-post-btn">
              <Plus className="w-4 h-4" />Add Social Post
            </button>
          </div>
        )}
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <Send className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No posts yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first social media post</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const Icon = platformIcon(post.platform);
            const s = statusConfig[post.status] || statusConfig.draft;
            const SIcon = s.icon;
            return (
              <div key={post.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`post-card-${post.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${platformColor(post.platform)} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{post.title}</h3>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`} data-testid={`post-status-${post.id}`}>
                          <SIcon className="w-3 h-3" />{s.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{post.caption}</p>
                      {post.hashtags && <div className="flex items-center gap-1.5 mt-2 text-xs text-violet-500"><Hash className="w-3 h-3" />{post.hashtags}</div>}
                      {post.feedback && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800" data-testid={`post-feedback-${post.id}`}>
                          <span className="font-medium">Feedback:</span> {post.feedback}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                        {post.scheduled_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{new Date(post.scheduled_date).toLocaleDateString()}</span>}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Created {new Date(post.created_at).toLocaleDateString()}</span>
                        {post.updated_at !== post.created_at && <span>Updated {new Date(post.updated_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Admin/Team actions */}
                    {!isClient && post.status === 'draft' && (
                      <button onClick={() => notifyApproval(post.id)} className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors" data-testid={`notify-${post.id}`}>
                        Notify for Approval
                      </button>
                    )}
                    {!isClient && (
                      <>
                        <button onClick={() => { setEditingPost(post); setShowForm(true); }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`edit-post-${post.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(post.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-post-${post.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                    {/* Client actions */}
                    {isClient && post.status === 'pending_approval' && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => approvePost(post.id)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors" data-testid={`approve-${post.id}`}>
                          <ThumbsUp className="w-3.5 h-3.5" />Approve
                        </button>
                        <button onClick={() => setFeedbackPost(post.id)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors" data-testid={`suggest-${post.id}`}>
                          <MessageSquare className="w-3.5 h-3.5" />Suggest Changes
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <PostFormModal post={editingPost} firmId={firmId} onClose={() => { setShowForm(false); setEditingPost(null); }} onSave={() => { setShowForm(false); setEditingPost(null); fetchPosts(); }} />}
      {feedbackPost && <FeedbackModal onClose={() => setFeedbackPost(null)} onSubmit={submitFeedback} />}
    </div>
  );
}

// ─── CONTENT CALENDAR TAB ───
function CalendarTab({ firmId, user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const token = localStorage.getItem('token');

  useEffect(() => { fetchPosts(); }, [firmId]);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/social/posts/${firmId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const filtered = posts.filter(p => {
    if (filterPlatform !== 'all' && p.platform !== filterPlatform) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    return true;
  }).sort((a, b) => new Date(a.scheduled_date || a.created_at) - new Date(b.scheduled_date || b.created_at));

  if (loading) return <div className="text-center py-12 text-slate-500">Loading calendar...</div>;

  return (
    <div className="space-y-6" data-testid="social-calendar">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Content Calendar</h2>
        <div className="flex items-center gap-3">
          <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)} className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/30" data-testid="filter-platform">
            <option value="all">All Platforms</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/30" data-testid="filter-status">
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusConfig[s]?.label || s}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No scheduled posts</p>
        </div>
      ) : (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="calendar-table">
            <thead className="bg-slate-50/80 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Platform</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Post Title</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Scheduled Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((post) => {
                const Icon = platformIcon(post.platform);
                const s = statusConfig[post.status] || statusConfig.draft;
                const SIcon = s.icon;
                return (
                  <tr key={post.id} className="hover:bg-violet-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${platformColor(post.platform)} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">{post.platform}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{post.title}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
                        <SIcon className="w-3 h-3" />{s.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {post.scheduled_date ? new Date(post.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───
import SocialMediaPaywallModal from './SocialMediaPaywallModal';
import AddonEntitlements from './AddonEntitlements';

export default function SocialMedia({ user, selectedClient }) {
  const [activeTab, setActiveTab] = useState('posts');
  const firmId = selectedClient?.firm_id || user.firm?.id || user.firm_id;
  const isClient = user.role === 'client';

  const [smActive, setSmActive] = useState(null); // null = loading, true/false = resolved
  const [paywallOpen, setPaywallOpen] = useState(false);

  const refreshSmState = async () => {
    if (!firmId) return null;
    try {
      const res = await axios.get(`${API}/me/billing`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = res.data || {};
      const ac = data.plan_config?.addons_config || {};
      const active = !!(data.social_media_active || ac.social_media === 'enabled' || data.manual_override_enabled);
      setSmActive(active);
      return active;
    } catch (_e) { setSmActive(false); return false; }
  };

  useEffect(() => {
    if (!isClient) { setSmActive(true); return; } // staff/admin always allowed
    refreshSmState();
  }, [firmId, isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open paywall when client lands here without an active plan
  useEffect(() => {
    if (smActive === false && isClient) setPaywallOpen(true);
    if (smActive === true) setPaywallOpen(false);
  }, [smActive, isClient]);

  // Post-Stripe-checkout polling - same pattern as Website Creation
  useEffect(() => {
    if (!firmId || !isClient) return undefined;
    if (smActive) return undefined;
    let stamp;
    try { stamp = localStorage.getItem('social_media_paying_at'); } catch (_e) { stamp = null; }
    if (!stamp) return undefined;
    const ageMs = Date.now() - new Date(stamp).getTime();
    if (Number.isNaN(ageMs) || ageMs > 1000 * 60 * 30) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const ok = await refreshSmState();
      if (ok) {
        try { localStorage.removeItem('social_media_paying_at'); } catch (_e) { /* ignore */ }
        return;
      }
      if (attempts >= 20) return;
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [firmId, isClient, smActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'account-access', label: 'Account Access' },
    { id: 'posts', label: 'Posts' },
    { id: 'calendar', label: 'Content Calendar' },
  ];

  if (!firmId) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Select a client to manage social media.</p>
      </div>
    );
  }

  // While we resolve the addon state, show a tiny loader (no flash of unauth UI)
  if (smActive === null) {
    return <div className="text-center py-16 text-slate-400" data-testid="social-media-loading">Loading social media…</div>;
  }

  const isLocked = smActive === false && isClient;

  return (
    <div className="space-y-6 animate-fadeInUp relative" data-testid="social-media-module">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Social Media</h1>
        <p className="text-slate-500 mt-1">Manage social accounts, posts, and content calendar</p>
      </div>

      <AddonEntitlements firmId={firmId} addonKey="social_media" />

      {/* Underlying view stays visible-but-locked when no plan */}
      <div className={isLocked ? 'pointer-events-none opacity-50 select-none' : ''} aria-disabled={isLocked} data-testid="social-media-locked-overlay-target">
        <div className="flex items-center gap-1 bg-slate-100/80 rounded-xl p-1" data-testid="social-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => !isLocked && setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`social-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'account-access' && <AccountAccess firmId={firmId} user={user} />}
        {activeTab === 'posts' && <PostsTab firmId={firmId} user={user} />}
        {activeTab === 'calendar' && <CalendarTab firmId={firmId} user={user} />}
      </div>

      {/* Locked CTA banner so the user can re-open the paywall after dismissing */}
      {isLocked && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-5 py-3 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 shadow-2xl shadow-violet-900/40 flex items-center gap-3" data-testid="social-media-locked-cta">
          <span className="text-white text-sm font-semibold">Activate Social Media to start posting</span>
          <button onClick={() => setPaywallOpen(true)} className="px-3 py-1.5 rounded-lg bg-white text-violet-700 text-xs font-bold hover:bg-violet-50 transition" data-testid="social-media-reopen-paywall">
            Choose plan
          </button>
        </div>
      )}

      <SocialMediaPaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
}
